<template>
  <header class="app-topbar">
    <div class="app-topbar__left">
      <button
        class="app-topbar__mobile-menu"
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#mobileNavigation"
        aria-controls="mobileNavigation"
        aria-label="Abrir menú"
        title="Abrir menú"
      >
        <i class="fa-solid fa-bars" aria-hidden="true"></i>
      </button>
      <button
        class="app-topbar__desktop-toggle"
        type="button"
        :aria-label="collapsed ? 'Expandir menú lateral' : 'Contraer menú lateral'"
        :title="collapsed ? 'Expandir menú' : 'Contraer menú'"
        @click="$emit('toggle')"
      >
        <i class="fa-solid fa-bars-staggered" aria-hidden="true"></i>
      </button>
      <router-link class="app-topbar__mobile-brand" to="/dashboard">DocBrain</router-link>
      <div class="app-topbar__title">
        <h1>{{ pageTitle }}</h1>
        <span v-if="company">{{ company }}</span>
      </div>
    </div>

    <div class="app-topbar__right">
      <div v-if="canSwitchOwnerScope && owners.length" class="app-topbar__owner">
        <label for="owner-scope-select">Empresa activa</label>
        <select id="owner-scope-select" class="form-select form-select-sm" :value="selectedOwnerId" @change="changeOwner">
          <option v-for="owner in owners" :key="owner.id" :value="owner.id">
            {{ ownerLabel(owner) }}
          </option>
        </select>
      </div>
      <UserMenu :user="user" :show-label="true" @logout="$emit('logout')" />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api from '@/api'
import UserMenu from '@/components/UserMenu.vue'
import type { AuthUser } from '@/store'

type Owner = { id: string, email: string, full_name?: string, organization?: string | null }

const props = defineProps<{ collapsed: boolean, user: AuthUser | null }>()
defineEmits<{ (event: 'toggle'): void, (event: 'logout'): void }>()
const route = useRoute()
const router = useRouter()
const store = useStore()
const owners = ref<Owner[]>([])

const pageTitle = computed(() => typeof route.meta.title === 'string' ? route.meta.title : 'DocBrain')
const company = computed(() =>
  props.user?.organization?.trim() ||
  props.user?.receipt_profile?.business_name?.trim() ||
  props.user?.receipt_profile?.legal_name?.trim() ||
  ''
)
const canSwitchOwnerScope = computed(() => store.getters.canSwitchOwnerScope)
const selectedOwnerId = computed(() => store.getters.selectedOwnerId || props.user?.owner_id || props.user?.id || '')
const ownerLabel = (owner: Owner) =>
  [owner.full_name?.trim() || owner.email, owner.organization?.trim()].filter(Boolean).join(' · ')

const loadOwners = async () => {
  if (!canSwitchOwnerScope.value) {
    owners.value = []
    return
  }
  try {
    const response = await api.get('/users/switchable-owners')
    owners.value = Array.isArray(response.data) ? response.data : []
  } catch {
    owners.value = []
  }
}

const changeOwner = (event: Event) => {
  store.commit('setSelectedOwnerId', (event.target as HTMLSelectElement).value)
  router.go(0)
}

onMounted(loadOwners)
watch(canSwitchOwnerScope, loadOwners)
</script>

